import React, {
  useCallback,
  useMemo,
  useState,
} from "react";

import Cropper from "react-easy-crop";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

/* ------------------------------------------------ */
/* Constants */
/* ------------------------------------------------ */

const DPI = 300;

const DEFAULT_WIDTH_INCH = 2;
const DEFAULT_HEIGHT_INCH = 2;

/* ------------------------------------------------ */
/* Utilities */
/* ------------------------------------------------ */

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.crossOrigin = "anonymous";
    image.src = src;

    image.onload = () => resolve(image);
    image.onerror = reject;
  });
};

const cropImage = async (
  imageSrc,
  cropAreaPixels,
  outputWidth,
  outputHeight
) => {
  const image = await loadImage(imageSrc);

  const canvas = document.createElement("canvas");

  const context = canvas.getContext("2d");

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  context.drawImage(
    image,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/png");
  });
};

/* ------------------------------------------------ */
/* Component */
/* ------------------------------------------------ */

const ImageCropper = ({
  open,
  imageUrl,
  onClose,
  onCropComplete,
}) => {
  /* ---------------------------------------------- */
  /* Crop states */
  /* ---------------------------------------------- */

  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  });

  const [zoom, setZoom] = useState(1);

  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState(null);

  /* ---------------------------------------------- */
  /* Size states */
  /* ---------------------------------------------- */

  const [widthInch, setWidthInch] =
    useState(DEFAULT_WIDTH_INCH);

  const [heightInch, setHeightInch] =
    useState(DEFAULT_HEIGHT_INCH);

  /* ---------------------------------------------- */
  /* Output size */
  /* ---------------------------------------------- */

  const outputWidth = useMemo(() => {
    return Math.round(widthInch * DPI);
  }, [widthInch]);

  const outputHeight = useMemo(() => {
    return Math.round(heightInch * DPI);
  }, [heightInch]);

  /* ---------------------------------------------- */
  /* Aspect ratio */
  /* ---------------------------------------------- */

  const aspect = useMemo(() => {
    return outputWidth / outputHeight;
  }, [outputWidth, outputHeight]);

  /* ---------------------------------------------- */
  /* Responsive crop box */
  /* ---------------------------------------------- */

  const cropSize = useMemo(() => {
    const maxPreview = 350;

    if (aspect >= 1) {
      return {
        width: maxPreview,
        height: maxPreview / aspect,
      };
    }

    return {
      width: maxPreview * aspect,
      height: maxPreview,
    };
  }, [aspect]);

  /* ---------------------------------------------- */
  /* Crop complete */
  /* ---------------------------------------------- */

  const handleCropComplete =
    useCallback((_, croppedPixels) => {
      setCroppedAreaPixels(croppedPixels);
    }, []);

  /* ---------------------------------------------- */
  /* Save */
  /* ---------------------------------------------- */

  const handleSave = async () => {
    try {
      if (!croppedAreaPixels) return;

      const croppedBlob =
        await cropImage(
          imageUrl,
          croppedAreaPixels,
          outputWidth,
          outputHeight
        );

      onCropComplete?.(croppedBlob);

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  /* ---------------------------------------------- */
  /* Render */
  /* ---------------------------------------------- */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Image Cropper
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* ---------------------------------- */}
          {/* Size Inputs */}
          {/* ---------------------------------- */}

          <Stack
            direction="row"
            spacing={2}
          >
            <Box>
              <Typography>Width (inch)</Typography>
              <TextField
                type="number"
                value={widthInch}
                onChange={(e) =>
                  setWidthInch(
                    Math.max(
                      0.1,
                      Number(e.target.value) || 0.1
                    )
                  )
                }
                inputProps={{
                  min: 0.1,
                  step: 0.1,
                }}
                fullWidth
              />
            </Box>

            <Box>
              <Typography>Height (inch)</Typography>
              <TextField
                type="number"
                value={heightInch}
                onChange={(e) =>
                  setHeightInch(
                    Math.max(
                      0.1,
                      Number(e.target.value) || 0.1
                    )
                  )
                }
                inputProps={{
                  min: 0.1,
                  step: 0.1,
                }}
                fullWidth
              />
            </Box>
          </Stack>

          {/* ---------------------------------- */}
          {/* Output Info */}
          {/* ---------------------------------- */}

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              DPI: {DPI}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Export Size:
              {" "}
              {outputWidth}px ×
              {" "}
              {outputHeight}px
            </Typography>
          </Box>

          {/* ---------------------------------- */}
          {/* Cropper */}
          {/* ---------------------------------- */}

          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "60vh",
              minHeight: 450,
              background: "#111",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropSize={cropSize}
              cropShape="rect"
              showGrid
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={
                handleCropComplete
              }
            />
          </Box>

          {/* ---------------------------------- */}
          {/* Zoom */}
          {/* ---------------------------------- */}

          <Box>
            <Typography gutterBottom>
              Zoom
            </Typography>

            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) =>
                setZoom(
                  Array.isArray(value)
                    ? value[0]
                    : value
                )
              }
            />
          </Box>
        </Stack>
      </DialogContent>

      {/* ------------------------------------ */}
      {/* Actions */}
      {/* ------------------------------------ */}

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!croppedAreaPixels}
        >
          Save Crop
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper;